import { z } from "zod";
import { auditEventSchema, type AuditScope } from "./audit";
import { listQuerySchema, listResponseSchema } from "./pagination";
import { ROLES, type Role } from "./roles";

/**
 * The company-wide activity feed.
 *
 * One paged, reverse-chronological read over `audit_events` — every scope at once, filtered
 * by the reader's role in the query's `where` rather than in presentation. The presentation
 * itself is the record histories' own (`features/audit`), so the feed can never tell a
 * different story than the record it is about.
 */

/**
 * The scope groups a reader chooses between.
 *
 * Three rather than four, because `stage` and `setting` are one question — "what has been
 * done to the shape of the board" — and nobody thinks of a stage rename and a threshold
 * change as different kinds of event.
 */
export const ACTIVITY_CATEGORIES = {
  opportunities: "opportunities",
  administration: "administration",
  configuration: "configuration",
} as const;

export type ActivityCategory = (typeof ACTIVITY_CATEGORIES)[keyof typeof ACTIVITY_CATEGORIES];

export const ACTIVITY_CATEGORY_VALUES = Object.values(ACTIVITY_CATEGORIES) as [
  ActivityCategory,
  ...ActivityCategory[],
];

export const activityCategorySchema = z.enum(ACTIVITY_CATEGORY_VALUES);

/** Which audit scopes each category selects. Exhaustive by type. */
export const ACTIVITY_CATEGORY_SCOPES: Record<ActivityCategory, readonly AuditScope[]> = {
  [ACTIVITY_CATEGORIES.opportunities]: ["opportunity"],
  [ACTIVITY_CATEGORIES.administration]: ["user"],
  [ACTIVITY_CATEGORIES.configuration]: ["stage", "setting"],
};

/**
 * What each role may read, as one `scope ∈ (…)` predicate.
 *
 * Everyone sees the events that shape the shared board — opportunities, stages, settings —
 * because those are the events that explain why the board looks the way it does. Only the
 * Head of Sales additionally sees the **user** scope: the administrative boundary the
 * invitation increment drew deliberately, and the timing of administration is itself
 * administrative.
 *
 * This is the whole visibility rule, and it is a **session** property. It is never a request
 * parameter — a category filter can narrow what a reader sees within this set, and can never
 * widen it.
 */
export const ROLE_VISIBLE_AUDIT_SCOPES: Record<Role, readonly AuditScope[]> = {
  [ROLES.salesManager]: ["opportunity", "stage", "setting"],
  [ROLES.headOfSales]: ["opportunity", "user", "stage", "setting"],
};

/** Whether a category has anything in it for this role — what hides the option client-side. */
export function isCategoryVisibleTo(role: Role, category: ActivityCategory): boolean {
  const visible: readonly AuditScope[] = ROLE_VISIBLE_AUDIT_SCOPES[role];
  return ACTIVITY_CATEGORY_SCOPES[category].some((scope) => visible.includes(scope));
}

/**
 * `page`, `perPage`, `actor`, `category` — and deliberately no `scope` and no `role`.
 *
 * `q` and `sort` ride in from `listQuerySchema` because `useServerTable` emits them; the feed
 * allowlists only its one ordering (`occurredAt`) and ignores free text, since an audit row's
 * searchable content is display values stamped at write time rather than a document.
 */
export const activityQuerySchema = listQuerySchema.extend({
  /** One user id. The question is "what has X been doing", which is singular. */
  actor: z.string().uuid().optional(),
  category: activityCategorySchema.optional(),
  /**
   * Full-text over the feed: the actor's name, the record the event happened to, and the
   * values the event itself recorded. Folded for case and diacritics server-side, so "skoda"
   * finds Škoda — see `foldForSearch` in the API.
   */
  q: z.string().trim().min(1).optional(),
});

export type ActivityQuery = z.infer<typeof activityQuerySchema>;

/**
 * One feed row: the event envelope, plus the display names a cross-record feed needs and a
 * per-record timeline does not.
 *
 * Joined at **read** time, unlike `oldValue`/`newValue` which are stamped at write time. The
 * distinction is deliberate and matches every existing timeline: what an event *says* happened
 * must not be rewritten by a later rename, but the record it points *at* has to be findable
 * under its current name.
 */
export const activityItemSchema = auditEventSchema.extend({
  /** Which of the four scopes this row belongs to — derived from the populated column. */
  scope: z.enum(["opportunity", "user", "stage", "setting"]),
  /** The record's current name: an opportunity's, a person's, a stage's, a setting's key. */
  recordLabel: z.string().nullable(),
  /**
   * The client an opportunity belongs to, for the rows that have one.
   *
   * A feed names records out of context — "Data warehouse assessment" is two clients' worth of
   * ambiguity on its own, and the board never shows an opportunity without its client either.
   */
  recordClientName: z.string().nullable(),
  /** The record's URL identity, where it has one. Stages and settings do not. */
  recordSlug: z.string().nullable(),
  /**
   * The opportunity this row is about no longer exists.
   *
   * The feed shows a deleted record's events — hiding a deletion's trail from a feed of
   * record-changes would be the opposite of its job — so a row has to be able to say so.
   */
  recordDeleted: z.boolean(),
});

export type ActivityItem = z.infer<typeof activityItemSchema>;

export const activityResponseSchema = listResponseSchema(activityItemSchema);

export type ActivityResponse = z.infer<typeof activityResponseSchema>;

/**
 * The table id, and therefore the URL namespace: `activity:category`, `activity:actor`.
 * Shared so the screen, its tests and the e2e leg cannot spell it three ways.
 */
export const ACTIVITY_TABLE_ID = "activity";
